import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import { roleMeta, Avatar } from "@/components/kit";
import {
  House, Users, Barbell, CalendarBlank, GraduationCap, ChatCircleDots,
  UserCircle, SignOut, Heartbeat, Megaphone, SignIn, Brain, FolderSimple,
} from "@phosphor-icons/react";

const navByRole = {
  athletic_director: [
    { to: "/app", label: "Dashboard", icon: House, end: true },
    { to: "/app/people", label: "People", icon: Users },
    { to: "/app/teams", label: "Teams", icon: Barbell },
    { to: "/app/schedule", label: "Schedule", icon: CalendarBlank },
    { to: "/app/academics", label: "Academics", icon: GraduationCap },
    { to: "/app/behavior", label: "Behavior", icon: Brain },
    { to: "/app/wellness", label: "Wellness", icon: Heartbeat },
    { to: "/app/checkins", label: "Check-ins", icon: SignIn },
    { to: "/app/files", label: "Files", icon: FolderSimple },
    { to: "/app/messages", label: "Messages", icon: ChatCircleDots },
  ],
  coach: [
    { to: "/app", label: "Dashboard", icon: House, end: true },
    { to: "/app/teams", label: "My Teams", icon: Barbell },
    { to: "/app/schedule", label: "Schedule", icon: CalendarBlank },
    { to: "/app/academics", label: "Athletes", icon: GraduationCap },
    { to: "/app/behavior", label: "Behavior", icon: Brain },
    { to: "/app/wellness", label: "Wellness", icon: Heartbeat },
    { to: "/app/checkins", label: "Check-ins", icon: SignIn },
    { to: "/app/files", label: "Files", icon: FolderSimple },
    { to: "/app/messages", label: "Messages", icon: ChatCircleDots },
  ],
  teacher: [
    { to: "/app", label: "Dashboard", icon: House, end: true },
    { to: "/app/academics", label: "Academics", icon: GraduationCap },
    { to: "/app/behavior", label: "Behavior", icon: Brain },
    { to: "/app/files", label: "Files", icon: FolderSimple },
    { to: "/app/messages", label: "Messages", icon: ChatCircleDots },
  ],
  athlete: [
    { to: "/app", label: "Dashboard", icon: House, end: true },
    { to: "/app/wellness", label: "Wellness", icon: Heartbeat },
    { to: "/app/checkins", label: "Check-ins", icon: SignIn },
    { to: "/app/schedule", label: "Schedule", icon: CalendarBlank },
    { to: "/app/academics", label: "Academics", icon: GraduationCap },
    { to: "/app/files", label: "Files", icon: FolderSimple },
    { to: "/app/messages", label: "Messages", icon: ChatCircleDots },
  ],
  parent: [
    { to: "/app", label: "Dashboard", icon: House, end: true },
    { to: "/app/schedule", label: "Schedule", icon: CalendarBlank },
    { to: "/app/academics", label: "Academics", icon: GraduationCap },
    { to: "/app/behavior", label: "Behavior", icon: Brain },
    { to: "/app/checkins", label: "Check-ins", icon: SignIn },
    { to: "/app/files", label: "Files", icon: FolderSimple },
    { to: "/app/messages", label: "Messages", icon: ChatCircleDots },
  ],
};

export default function AppShell({ children }) {
  const { user, logout, enteredSchool, exitSchool } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [school, setSchool] = useState(null);
  const links = navByRole[user?.role] || navByRole.athlete;
  const rm = roleMeta[user?.role] || {};
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const handleExitSchool = async () => { await exitSchool(); navigate("/superadmin"); };

  useEffect(() => {
    api.get("/my-school").then((r) => setSchool(r.data)).catch(() => {});
  }, [enteredSchool]);

  useEffect(() => {
    const fetchUnread = () => api.get("/messages/unread/count").then((r) => setUnread(r.data.count)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = () => { logout(); navigate("/login"); };

  const renderBadge = (label) => label === "Messages" && unread > 0 ? (
    <span className="ml-auto bg-[#FF3B30] text-white text-[10px] font-bold rounded-sm px-1.5 py-0.5" data-testid="unread-badge">{unread}</span>
  ) : null;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#0A0A0A]">
      <aside className="hidden md:flex md:flex-col w-64 border-r border-white/10 bg-[#0A0A0A] sticky top-0 h-screen">
        <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Barbell size={28} weight="fill" color="#FF3B30" />
            <span className="font-head text-2xl font-extrabold uppercase tracking-tight">Athlete<span className="text-[#FF3B30]">Hub</span></span>
          </div>
          <NotificationBell />
        </div>
        {school && (
          <div className="flex items-center gap-3 px-6 py-3 border-b border-white/10" data-testid="school-brand">
            {school.logo_url ? (
              <img src={`${BACKEND}${school.logo_url}`} alt={school.name} className="w-9 h-9 rounded-sm object-contain bg-white/5 border border-white/10" />
            ) : (
              <span className="w-9 h-9 rounded-sm flex items-center justify-center text-sm font-bold" style={{ background: `${school.primary_color}22`, color: school.primary_color }}>{school.name?.[0]}</span>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate" data-testid="school-name">{school.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373]">{rm.label}</div>
            </div>
          </div>
        )}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-semibold uppercase tracking-widest transition-colors ${isActive ? "text-white" : "text-[#A3A3A3] hover:text-white hover:bg-white/5"}`}
              style={({ isActive }) => isActive ? { background: school?.primary_color || "#FF3B30" } : undefined}>
              <l.icon size={20} weight="bold" /> {l.label} {renderBadge(l.label)}
            </NavLink>
          ))}
          <NavLink to="/app/profile" data-testid="nav-profile"
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-semibold uppercase tracking-widest transition-colors ${isActive ? "bg-[#FF3B30] text-white" : "text-[#A3A3A3] hover:text-white hover:bg-white/5"}`}>
            <UserCircle size={20} weight="bold" /> Profile
          </NavLink>
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <Avatar name={user?.name} color={rm.color} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: rm.color }}>{rm.label}</div>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-semibold uppercase tracking-widest text-[#A3A3A3] hover:text-white hover:bg-white/5 transition-colors">
            <SignOut size={20} weight="bold" /> Logout
          </button>
        </div>
      </aside>

      <header className="md:hidden flex items-center justify-between px-4 h-16 border-b border-white/10 sticky top-0 z-40 bg-black/70 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <Barbell size={24} weight="fill" color="#FF3B30" />
          <span className="font-head text-xl font-extrabold uppercase">Athlete<span className="text-[#FF3B30]">Hub</span></span>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <NavLink to="/app/profile" className="text-[#A3A3A3]"><UserCircle size={22} weight="bold" /></NavLink>
          <button onClick={handleLogout} data-testid="logout-button-mobile" className="text-[#A3A3A3]"><SignOut size={22} weight="bold" /></button>
        </div>
      </header>

      <main className="flex-1 min-w-0 pb-24 md:pb-0">
        {enteredSchool && (
          <div className="flex items-center gap-3 px-4 md:px-8 py-2.5 bg-[#0A84FF]/15 border-b border-[#0A84FF]/30 text-sm" data-testid="impersonation-banner">
            <span className="text-[#0A84FF] font-bold uppercase tracking-widest text-xs">Super Admin</span>
            <span className="text-white truncate">Managing <span className="font-semibold">{enteredSchool.name}</span></span>
            <button onClick={handleExitSchool} data-testid="exit-school-button"
              className="ml-auto text-xs font-bold uppercase tracking-widest text-[#0A84FF] hover:text-white transition-colors">Exit ✕</button>
          </div>
        )}
        {children}
      </main>

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex border-t border-white/10 bg-black/80 backdrop-blur-xl overflow-x-auto">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-mobile`}
            className={({ isActive }) => `flex-1 min-w-[64px] flex flex-col items-center gap-1 py-3 text-[9px] uppercase tracking-wider font-semibold transition-colors relative ${isActive ? "text-[#FF3B30]" : "text-[#737373]"}`}>
            <l.icon size={20} weight="bold" />
            {l.label === "Messages" && unread > 0 && <span className="absolute top-1 right-1/4 w-2 h-2 bg-[#FF3B30] rounded-full" />}
            {l.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
